import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxiosPrivate } from "../useAxiosPrivate";

export default function useExtraItemQueries() {
    const axiosPrivate = useAxiosPrivate();
    const queryClient = useQueryClient();

    // ------------------------------------------------------------
    // QUERIES
    // ------------------------------------------------------------

    // Get all ExtraItems for the business
    const extraItemsForBusinessQuery = useQuery({
        queryKey: ["extra-items", "business"],
        queryFn: () =>
            axiosPrivate
                .get("extra-items/get_for_business/")
                .then(r => r.data),
    });

    // Fetch single ExtraItem by ID
    const useGetExtraItemById = (id) => useQuery({
            queryKey: ["extra-items", id],
            queryFn: () =>
                axiosPrivate
                    .get(`extra-items/${id}/`)
                    .then(r => r.data),
            enabled: !!id,
        });

    const useExtraItemsForServiceQuery = (id) =>
        useQuery({
            queryKey: ["extra-items", id],
            queryFn: ({ queryKey }) => {
                const [, serviceId] = queryKey;
                return axiosPrivate
                    .get(`extra-items/get_for_service/?service=${serviceId}`)
                    .then((r) => r.data);
            },
            enabled: !!id, // prevents running until id exists
        });

    // ------------------------------------------------------------
    // MUTATIONS
    // ------------------------------------------------------------

    // Create ExtraItem (POST extra-items/)
    const createExtraItemMutation = useMutation({
        mutationFn: (formData) =>
            axiosPrivate
                .post("extra-items/", formData, {headers: {'Content-Type': 'multipart/form-data'}})
                .then(r => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries(["extra-items"]);
        },
    });

    // Add service to ExtraItem
    const addServiceMutation = useMutation({
        mutationFn: ({ id, service }) =>
            axiosPrivate
                .post("extra-items/add_service/", { id, service })
                .then(r => r.data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries(["extra-items", id]);
            // queryClient.invalidateQueries(["extra-items", "business"]);
        },
    });

    // Remove service from ExtraItem
    const removeServiceMutation = useMutation({
        mutationFn: ({ id, service }) =>
            axiosPrivate
                .post("extra-items/remove_service/", { id, service })
                .then(r => r.data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries(["extra-items", id]);
            // queryClient.invalidateQueries(["extra-items", "business"]);
        },
    });

    const updateExtraItemMutation = useMutation({
        mutationFn: ({id, data}) => axiosPrivate.patch(`extra-items/${id}/`, data, {headers: {'Content-Type': 'multipart/form-data'}}).then(r => r.data),
        onSuccess: (...args) => {
            queryClient.invalidateQueries(["extra-items"]);
        },
    });

    const deleteExtraItemMutation = useMutation({
        mutationFn: (id) => axiosPrivate.delete(`extra-items/${id}/`).then(r => r.data),
        onSuccess: (...args) => {
            queryClient.invalidateQueries(["extra-items"]);
        },
    });


    return {
        // Queries
        extraItemsForBusinessQuery,
        useGetExtraItemById,
        useExtraItemsForServiceQuery,

        // Mutations
        createExtraItemMutation,
        addServiceMutation,
        removeServiceMutation,
        updateExtraItemMutation,
        deleteExtraItemMutation,
    };
}
